import React from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import { useNavigate } from 'react-router-dom';
import PageLayout from '../components/common/PageLayout';
import { get, post } from '../utils/api';

function money(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);
}

function dateTime(v) {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleString();
}

function contributionIdOf(c) {
  const raw = c?.contributionId ?? c?.contribution_id ?? c?.id;
  const n = parseInt(String(raw == null ? '' : raw).trim(), 10);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function variationLabel(v) {
  if (!v) return '';
  const d = (v.display_name || '').trim();
  const s = (v.sku || '').trim();
  if (d && s) return `${d} (${s})`;
  return d || s || `ID ${v.id}`;
}

function formatVariationAmount(priceNum) {
  const n = Number(priceNum);
  if (!Number.isFinite(n)) return '';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);
}

function variationSelectLabel(v) {
  const base = variationLabel(v);
  const amt = formatVariationAmount(v?.price_num);
  if (amt) return `${base} — ${amt}`;
  return base;
}

function variationSelectLabelWithId(v) {
  if (!v) return '';
  return `${variationSelectLabel(v)} (ID: ${v.id})`;
}

function sortVariationsForDialog(list) {
  return [...list].sort((a, b) => {
    const pa = Number(a?.price_num);
    const pb = Number(b?.price_num);
    const na = Number.isFinite(pa) ? pa : 0;
    const nb = Number.isFinite(pb) ? pb : 0;
    if (na !== nb) return na - nb;
    return Number(a?.id || 0) - Number(b?.id || 0);
  });
}

/** Same success rule as mapping migration: completed + civi cancel success + order id. */
function isMigrationFullySuccessful(r) {
  const mig = String(r?.migration_status || '').trim().toLowerCase();
  const civi = String(r?.civi_cancel_status || '').trim().toLowerCase();
  return mig === 'completed' && civi === 'success' && r?.wc_order_id != null;
}

function hasMappingForRetry(r) {
  const mid = r?.mapping_id;
  return mid != null && String(mid).trim() !== '' && Number(mid) > 0;
}

function migratedRowToContributionLike(r) {
  return {
    ...r,
    contribution_id: r.contribution_id,
    contributionId: r.contribution_id,
    contribution_date: r.contribution_date,
    contributionDate: r.contribution_date,
    receiveDate: r.contribution_date,
    receive_date: r.contribution_date,
    customer_display_name: r.customer_display_name,
    customerEmail: r.customer_email,
    email: r.customer_email,
    userId: r.wp_user_id,
    user_id: r.wp_user_id,
    payment_method: r.payment_method,
    paymentMethod: r.payment_method,
    source: r.source,
    Source: r.source,
    contributionAmount: r.contribution_amount,
    total_amount: r.contribution_amount,
    amount: r.contribution_amount,
  };
}

function mappingContextFromRow(r) {
  const ft = Number(r?.financial_type_id);
  const cpRaw = Number(r?.contribution_page_id);
  const cp = Number.isFinite(cpRaw) && cpRaw >= 0 ? cpRaw : 0;
  const id = Number(r?.mapping_id);
  if (!Number.isFinite(ft) || ft < 0 || !Number.isFinite(id) || id <= 0) return null;
  return { id, financial_type_id: ft, contribution_page_id: cp };
}

export default function ManageCiviContributionsMigratedPage() {
  const navigate = useNavigate();
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [q, setQ] = React.useState('');
  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(25);
  const [status, setStatus] = React.useState('all');
  const [totalRecords, setTotalRecords] = React.useState(0);
  const [totalPages, setTotalPages] = React.useState(0);
  const [detailOpen, setDetailOpen] = React.useState(false);
  const [detailLoading, setDetailLoading] = React.useState(false);
  const [detailRow, setDetailRow] = React.useState(null);
  const [detailError, setDetailError] = React.useState('');

  const [selectedContribIds, setSelectedContribIds] = React.useState([]);
  const [recordMigOpen, setRecordMigOpen] = React.useState(false);
  const [recordMigRows, setRecordMigRows] = React.useState([]);
  const [recordMigMappingContext, setRecordMigMappingContext] = React.useState(null);
  const [recordMigProductInput, setRecordMigProductInput] = React.useState('');
  const [recordMigProductOptions, setRecordMigProductOptions] = React.useState([]);
  const [recordMigSelectedProduct, setRecordMigSelectedProduct] = React.useState(null);
  const [recordMigVariations, setRecordMigVariations] = React.useState([]);
  const [recordMigVariationId, setRecordMigVariationId] = React.useState('');
  const [recordMigSubmitting, setRecordMigSubmitting] = React.useState(false);
  const [recordMigError, setRecordMigError] = React.useState('');
  const [recordMigResult, setRecordMigResult] = React.useState(null);
  const [recordMigRunState, setRecordMigRunState] = React.useState('');
  const [recordMigSummary, setRecordMigSummary] = React.useState(null);
  const [recordMigStatusById, setRecordMigStatusById] = React.useState({});
  const [validationError, setValidationError] = React.useState('');

  const load = React.useCallback(async (nextPage = page, nextQ = q, nextStatus = status, nextPageSize = pageSize) => {
    setLoading(true);
    setError('');
    setValidationError('');
    try {
      const res = await get('/api/manage/civi-contributions-migrated', {
        page: nextPage,
        pageSize: nextPageSize,
        q: nextQ || undefined,
        status: nextStatus,
      });
      setRows(Array.isArray(res?.data) ? res.data : []);
      setPage(Number(res?.page || nextPage || 1));
      setTotalRecords(Number(res?.totalRecords || 0));
      setTotalPages(Number(res?.totalPages || 0));
    } catch (e) {
      setRows([]);
      setTotalRecords(0);
      setTotalPages(0);
      setError(e?.data?.error || e?.message || 'Failed to load migrated contributions');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, q, status]);

  React.useEffect(() => {
    const t = setTimeout(() => {
      setPage(1);
      load(1, q, status, pageSize);
    }, 350);
    return () => clearTimeout(t);
  }, [q, status, pageSize, load]);

  React.useEffect(() => {
    setSelectedContribIds([]);
  }, [page, status, q, pageSize]);

  React.useEffect(() => {
    if (!recordMigProductInput || recordMigProductInput.length < 2) {
      setRecordMigProductOptions([]);
      return;
    }
    let ignore = false;
    (async () => {
      try {
        const res = await get('/ldb/facets/products', { q: recordMigProductInput, limit: 80 });
        if (!ignore) setRecordMigProductOptions(Array.isArray(res?.products) ? res.products : []);
      } catch {
        if (!ignore) setRecordMigProductOptions([]);
      }
    })();
    return () => {
      ignore = true;
    };
  }, [recordMigProductInput]);

  React.useEffect(() => {
    if (!recordMigSelectedProduct?.id) {
      setRecordMigVariations([]);
      setRecordMigVariationId('');
      return;
    }
    let ignore = false;
    (async () => {
      try {
        const res = await get('/ldb/facets/variations', { product_id: String(recordMigSelectedProduct.id), limit: 300 });
        const list = Array.isArray(res?.variations) ? sortVariationsForDialog(res.variations) : [];
        if (!ignore) {
          setRecordMigVariations(list);
          if (!list.some((v) => String(v.id) === String(recordMigVariationId))) setRecordMigVariationId('');
        }
      } catch {
        if (!ignore) {
          setRecordMigVariations([]);
          setRecordMigVariationId('');
        }
      }
    })();
    return () => {
      ignore = true;
    };
  }, [recordMigSelectedProduct?.id]);

  const retryableRowsOnPage = React.useMemo(
    () => rows.filter((r) => !isMigrationFullySuccessful(r) && hasMappingForRetry(r)),
    [rows],
  );

  const selectionOnPage = React.useMemo(() => {
    const ids = retryableRowsOnPage.map((r) => contributionIdOf(migratedRowToContributionLike(r))).filter((n) => Number.isFinite(n));
    const selected = ids.filter((id) => selectedContribIds.includes(id));
    return {
      pageIds: ids,
      selectedCount: selected.length,
      allChecked: ids.length > 0 && selected.length === ids.length,
      indeterminate: selected.length > 0 && selected.length < ids.length,
    };
  }, [retryableRowsOnPage, selectedContribIds]);

  const toggleSelectAllOnPage = (checked) => {
    const pageIds = selectionOnPage.pageIds;
    if (!pageIds.length) return;
    if (checked) {
      setSelectedContribIds((prev) => Array.from(new Set([...prev, ...pageIds])));
      return;
    }
    setSelectedContribIds((prev) => prev.filter((id) => !pageIds.includes(id)));
  };

  const toggleContributionSelection = (contributionId, checked) => {
    if (!Number.isFinite(contributionId)) return;
    if (checked) {
      setSelectedContribIds((prev) => (prev.includes(contributionId) ? prev : [...prev, contributionId]));
      return;
    }
    setSelectedContribIds((prev) => prev.filter((id) => id !== contributionId));
  };

  const contributionRunStatus = React.useCallback(
    (contributionId) => {
      if (!Number.isFinite(contributionId)) return '';
      return String(recordMigStatusById[contributionId] || '').trim().toLowerCase();
    },
    [recordMigStatusById],
  );

  const renderContributionStatusIcon = (statusRaw) => {
    const st = String(statusRaw || '').trim().toLowerCase();
    if (st === 'processing' || st === 'queued') {
      return (
        <AutorenewIcon
          fontSize="small"
          color="info"
          sx={{
            animation: 'spin 1.1s linear infinite',
            '@keyframes spin': { from: { transform: 'rotate(0deg)' }, to: { transform: 'rotate(360deg)' } },
          }}
        />
      );
    }
    if (st === 'completed') {
      return <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />;
    }
    if (st === 'failed') {
      return <CancelIcon fontSize="small" sx={{ color: 'error.main' }} />;
    }
    return (
      <Typography variant="caption" color="text.secondary">
        —
      </Typography>
    );
  };

  const openRecordMigrationDialog = (list) => {
    setValidationError('');
    const mapped = Array.isArray(list) ? list.map(migratedRowToContributionLike).filter(Boolean) : [];
    if (!mapped.length) return;
    const ctx = mappingContextFromRow(list[0]);
    if (!ctx) {
      setValidationError('No product mapping row found for this financial type and contribution page. Add or fix mapping on Civi → Woo product mapping first.');
      return;
    }
    for (let i = 1; i < list.length; i += 1) {
      const c = mappingContextFromRow(list[i]);
      if (!c || c.id !== ctx.id || c.financial_type_id !== ctx.financial_type_id || c.contribution_page_id !== ctx.contribution_page_id) {
        setValidationError('Selected rows must share the same mapping (financial type and contribution page).');
        return;
      }
    }
    setRecordMigRows(mapped);
    setRecordMigMappingContext(ctx);
    setRecordMigOpen(true);
    setRecordMigError('');
    setRecordMigResult(null);
    setRecordMigSubmitting(false);
    setRecordMigSelectedProduct(null);
    setRecordMigProductInput('');
    setRecordMigProductOptions([]);
    setRecordMigVariations([]);
    setRecordMigVariationId('');
    setRecordMigRunState('');
    setRecordMigSummary(null);
    setRecordMigStatusById({});
  };

  const openSingleRecordMigration = (migratedRow) => {
    openRecordMigrationDialog([migratedRow]);
  };

  const openGroupMigrationFromSelection = () => {
    const byId = new Set(selectedContribIds);
    const list = rows.filter((r) => {
      if (!isMigrationFullySuccessful(r) && hasMappingForRetry(r)) {
        const cid = contributionIdOf(migratedRowToContributionLike(r));
        return Number.isFinite(cid) && byId.has(cid);
      }
      return false;
    });
    if (!list.length) {
      setValidationError('Select at least one row that is not fully migrated and has a product mapping.');
      return;
    }
    openRecordMigrationDialog(list);
  };

  const closeRecordMigrationDialog = () => {
    setRecordMigOpen(false);
    setRecordMigRows([]);
    setRecordMigMappingContext(null);
    setRecordMigError('');
    setRecordMigSubmitting(false);
    setRecordMigRunState('');
    setRecordMigSummary(null);
  };

  const canProceedRecordMigration = React.useMemo(() => {
    if (recordMigSubmitting) return false;
    if (!recordMigSelectedProduct?.id || recordMigRows.length === 0) return false;
    const queued = Number(recordMigSummary?.queued || 0);
    const processing = Number(recordMigSummary?.processing || 0);
    const failed = Number(recordMigSummary?.failed || 0);
    const inProgress =
      String(recordMigRunState || '').trim().toLowerCase() === 'running' || queued > 0 || processing > 0;
    if (inProgress) return false;
    const hasSummary = queued + processing + Number(recordMigSummary?.completed || 0) + failed > 0;
    const variationRequiredButMissing = recordMigVariations.length > 0 && String(recordMigVariationId || '').trim() === '';
    if (variationRequiredButMissing) return false;
    if (!hasSummary) return true;
    return failed > 0;
  }, [
    recordMigSubmitting,
    recordMigRunState,
    recordMigSelectedProduct,
    recordMigRows,
    recordMigSummary,
    recordMigVariations,
    recordMigVariationId,
  ]);

  const onProceedRecordMigration = async () => {
    if (!recordMigMappingContext?.id) return;
    const productId = Number(recordMigSelectedProduct?.id || 0);
    if (!productId) {
      setRecordMigError('Select a WooCommerce product.');
      return;
    }
    if (recordMigVariations.length > 0 && recordMigVariationId === '') {
      setRecordMigError('This product has variations — pick one.');
      return;
    }
    const contributionIds = recordMigRows.map(contributionIdOf).filter((n) => Number.isFinite(n));
    if (!contributionIds.length) {
      setRecordMigError('No valid contribution IDs selected.');
      return;
    }

    setRecordMigSubmitting(true);
    setRecordMigError('');
    try {
      const contributionPageId = (() => {
        const n = Number(recordMigMappingContext.contribution_page_id);
        return Number.isFinite(n) && n >= 0 ? n : 0;
      })();
      const baseBody = {
        financialTypeId: Number(recordMigMappingContext.financial_type_id),
        contributionPageId,
        wc_product_id: productId,
      };
      if (recordMigVariationId !== '') baseBody.wc_variation_id = Number(recordMigVariationId);

      setRecordMigRunState('running');
      setRecordMigResult(null);
      setRecordMigStatusById(Object.fromEntries(contributionIds.map((id) => [id, 'processing'])));
      setRecordMigSummary({
        queued: 0,
        processing: contributionIds.length,
        completed: 0,
        failed: 0,
      });

      const batchRes = await post(
        '/api/manage/civi-wc-product-mapping/migrate-contributions-batch',
        { ...baseBody, contribution_ids: contributionIds },
        { timeout: 0 },
      );
      const results = Array.isArray(batchRes?.results) ? batchRes.results : [];

      const statusMap = {};
      const outRows = [];
      for (let i = 0; i < results.length; i += 1) {
        const item = results[i];
        const cid = Number(item?.contribution_id);
        const row = item?.row || null;
        if (row) outRows.push(row);
        const migrationSucceeded =
          item?.migrationSucceeded === true
          || (
            String(row?.migration_status || '').trim().toLowerCase() === 'completed'
            && String(row?.civi_cancel_status || '').trim().toLowerCase() === 'success'
            && row?.wc_order_id != null
          );
        const st = migrationSucceeded ? 'completed' : 'failed';
        if (Number.isFinite(cid) && cid > 0) statusMap[cid] = st;
      }
      setRecordMigStatusById((prev) => ({ ...prev, ...statusMap }));
      setRecordMigSummary({
        queued: 0,
        processing: 0,
        completed: Number(batchRes?.summary?.completed ?? 0),
        failed: Number(batchRes?.summary?.failed ?? 0),
      });
      setRecordMigRunState('completed');
      setRecordMigResult({ data: outRows });
      setSelectedContribIds((prev) => prev.filter((id) => !contributionIds.includes(id)));
      await load(page, q, status, pageSize);
    } catch (e) {
      setRecordMigRunState('failed');
      const reqErr = e?.data?.error || e?.message || 'Failed to migrate selected contribution records';
      let reconciled = false;
      try {
        const mid = recordMigMappingContext.id;
        const rec = await get(
          `/api/manage/civi-wc-product-mapping/${encodeURIComponent(String(mid))}/migrated-contributions-by-ids`,
          { ids: contributionIds.join(',') },
        );
        const recRows = Array.isArray(rec?.data) ? rec.data : [];
        if (recRows.length > 0) {
          const byId = Object.fromEntries(recRows.map((row) => [Number(row.contribution_id), row]));
          const statusMap = {};
          let completed = 0;
          let failed = 0;
          for (const id of contributionIds) {
            const row = byId[id];
            if (!row) {
              statusMap[id] = 'failed';
              failed += 1;
              continue;
            }
            const ok =
              String(row?.migration_status || '').trim().toLowerCase() === 'completed'
              && String(row?.civi_cancel_status || '').trim().toLowerCase() === 'success'
              && row?.wc_order_id != null;
            statusMap[id] = ok ? 'completed' : 'failed';
            if (ok) completed += 1;
            else failed += 1;
          }
          setRecordMigStatusById((prev) => ({ ...prev, ...statusMap }));
          setRecordMigSummary({ queued: 0, processing: 0, completed, failed });
          setRecordMigResult({ data: recRows });
          setRecordMigError(`Request failed in browser, but backend reconciliation found completed=${completed}, failed=${failed}.`);
          reconciled = true;
        }
      } catch (_) {
        // best-effort
      }
      if (!reconciled) {
        setRecordMigError(reqErr);
        setRecordMigSummary({ queued: 0, processing: 0, completed: 0, failed: contributionIds.length });
        setRecordMigStatusById(Object.fromEntries(contributionIds.map((id) => [id, 'failed'])));
      }
      await load(page, q, status, pageSize);
    } finally {
      setRecordMigSubmitting(false);
    }
  };

  const openContributionDetails = async (contributionId) => {
    if (!contributionId) return;
    setDetailOpen(true);
    setDetailLoading(true);
    setDetailError('');
    setDetailRow(null);
    try {
      const res = await get(`/api/manage/civi-contributions-migrated/${encodeURIComponent(String(contributionId))}`);
      setDetailRow(res?.row || null);
    } catch (e) {
      setDetailError(e?.data?.error || e?.message || 'Failed to load contribution details');
    } finally {
      setDetailLoading(false);
    }
  };

  return (
    <PageLayout contentMaxWidth="xl" showHeaderImage={false} showOrgHeader>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <IconButton onClick={() => navigate('/manage/civi-wc-product-mapping')} aria-label="Back" size="small">
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              Migrated Contributions
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Search and review rows from civi_contributions_migrated.
            </Typography>
          </Box>
        </Stack>
      </Stack>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={{ mb: 2 }}>
        <TextField
          sx={{ minWidth: 320, maxWidth: 760 }}
          size="small"
          label="Search"
          placeholder="Customer, product, variation, contribution page, financial type, order id..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <ToggleButtonGroup
          size="small"
          color="primary"
          exclusive
          value={status}
          onChange={(_, v) => {
            if (!v) return;
            setStatus(v);
            setPage(1);
          }}
        >
          <ToggleButton value="all">All</ToggleButton>
          <ToggleButton value="completed">Completed</ToggleButton>
          <ToggleButton value="failed">Failed</ToggleButton>
        </ToggleButtonGroup>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel id="page-size-label">Page size</InputLabel>
          <Select
            labelId="page-size-label"
            label="Page size"
            value={String(pageSize)}
            onChange={(e) => {
              const v = parseInt(String(e.target.value || '25'), 10);
              setPageSize(Number.isFinite(v) ? v : 25);
              setPage(1);
            }}
          >
            <MenuItem value="25">25</MenuItem>
            <MenuItem value="50">50</MenuItem>
            <MenuItem value="100">100</MenuItem>
            <MenuItem value="200">200</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}
      {validationError && (
        <Typography color="error" sx={{ mb: 2 }}>
          {validationError}
        </Typography>
      )}

      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Total records: {totalRecords} • Page {page}{totalPages > 0 ? ` of ${totalPages}` : ''}
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <Button
            size="small"
            variant="contained"
            disabled={loading || selectedContribIds.length === 0}
            onClick={openGroupMigrationFromSelection}
          >
            Group migrate ({selectedContribIds.length})
          </Button>
          <Button
            size="small"
            variant="outlined"
            disabled={loading || page <= 1}
            onClick={() => load(Math.max(1, page - 1), q, status, pageSize)}
          >
            Previous
          </Button>
          <Button
            size="small"
            variant="outlined"
            disabled={loading || (totalPages > 0 && page >= totalPages)}
            onClick={() => load(page + 1, q, status, pageSize)}
          >
            Next
          </Button>
        </Stack>
      </Stack>

      <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 1, overflowX: 'auto' }}>
        <Table size="small" stickyHeader sx={{ minWidth: 2200 }}>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" sx={{ width: 48 }}>
                <Checkbox
                  size="small"
                  checked={selectionOnPage.allChecked}
                  indeterminate={selectionOnPage.indeterminate}
                  disabled={loading || retryableRowsOnPage.length === 0}
                  onChange={(e) => toggleSelectAllOnPage(!!e.target.checked)}
                  inputProps={{ 'aria-label': 'Select all retryable rows on this page' }}
                />
              </TableCell>
              <TableCell sx={{ width: 56 }}>Migrate</TableCell>
              <TableCell>Contribution ID</TableCell>
              <TableCell>WC Order ID</TableCell>
              <TableCell>Customer</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>WP User ID</TableCell>
              <TableCell>WC Customer ID</TableCell>
              <TableCell>Financial type</TableCell>
              <TableCell>Contribution page</TableCell>
              <TableCell>Woo product</TableCell>
              <TableCell>Woo variation</TableCell>
              <TableCell>Amount</TableCell>
              <TableCell>Payment method</TableCell>
              <TableCell>Check #</TableCell>
              <TableCell>Order status</TableCell>
              <TableCell>Migration status</TableCell>
              <TableCell>Civi cancel</TableCell>
              <TableCell>Migration date</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Updated</TableCell>
              <TableCell>Error</TableCell>
              <TableCell>Contribution date</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={24}>Loading...</TableCell>
              </TableRow>
            )}
            {!loading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={24}>No migrated contributions found.</TableCell>
              </TableRow>
            )}
            {!loading && rows.map((r) => (
              <TableRow key={r.id} hover>
                <TableCell padding="checkbox">
                  {(() => {
                    const canRetry = !isMigrationFullySuccessful(r) && hasMappingForRetry(r);
                    const failedNoMap = !isMigrationFullySuccessful(r) && !hasMappingForRetry(r);
                    const cid = contributionIdOf(migratedRowToContributionLike(r));
                    if (isMigrationFullySuccessful(r)) {
                      return null;
                    }
                    if (canRetry) {
                      return (
                        <Checkbox
                          size="small"
                          checked={Number.isFinite(cid) && selectedContribIds.includes(cid)}
                          onChange={(e) => toggleContributionSelection(cid, !!e.target.checked)}
                          disabled={!Number.isFinite(cid)}
                        />
                      );
                    }
                    if (failedNoMap) {
                      return (
                        <Tooltip title="No product mapping row for this financial type and contribution page.">
                          <span>
                            <Checkbox size="small" disabled />
                          </span>
                        </Tooltip>
                      );
                    }
                    return null;
                  })()}
                </TableCell>
                <TableCell>
                  {(() => {
                    const canRetry = !isMigrationFullySuccessful(r) && hasMappingForRetry(r);
                    const failedNoMap = !isMigrationFullySuccessful(r) && !hasMappingForRetry(r);
                    if (isMigrationFullySuccessful(r)) {
                      return null;
                    }
                    if (canRetry) {
                      return (
                        <Tooltip title="Migrate this record">
                          <span>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => openSingleRecordMigration(r)}
                              disabled={!contributionIdOf(migratedRowToContributionLike(r))}
                            >
                              <PlayCircleOutlineIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      );
                    }
                    if (failedNoMap) {
                      return (
                        <Tooltip title="Add a Civi → Woo mapping row for this financial type and contribution page first.">
                          <span>
                            <IconButton size="small" disabled>
                              <PlayCircleOutlineIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      );
                    }
                    return null;
                  })()}
                </TableCell>
                <TableCell>
                  {r.contribution_id ? (
                    <Link component="button" underline="hover" onClick={() => openContributionDetails(r.contribution_id)}>
                      {String(r.contribution_id)}
                    </Link>
                  ) : '—'}
                </TableCell>
                <TableCell>
                  {r.wc_order_id ? (
                    <Link component="button" underline="hover" onClick={() => navigate(`/orders/${r.wc_order_id}/edit`)}>
                      {String(r.wc_order_id)}
                    </Link>
                  ) : '—'}
                </TableCell>
                <TableCell>{r.customer_display_name || '—'}</TableCell>
                <TableCell>{r.customer_email || '—'}</TableCell>
                <TableCell>{r.wp_user_id ?? '—'}</TableCell>
                <TableCell>{r.wc_customer_id ?? '—'}</TableCell>
                <TableCell>{r.financial_type_name || '—'}</TableCell>
                <TableCell>{r.contribution_page_name || '—'}</TableCell>
                <TableCell>{r.wc_product_name ? `${r.wc_product_name} (${r.wc_product_id ?? '—'})` : (r.wc_product_id ?? '—')}</TableCell>
                <TableCell>{r.wc_variation_name ? `${r.wc_variation_name} (${r.wc_variation_id ?? '—'})` : (r.wc_variation_id ?? '—')}</TableCell>
                <TableCell>{money(r.contribution_amount)}</TableCell>
                <TableCell>{r.payment_method || '—'}</TableCell>
                <TableCell>{r.check_number || '—'}</TableCell>
                <TableCell>{r.order_status || '—'}</TableCell>
                <TableCell>{r.migration_status || '—'}</TableCell>
                <TableCell>{r.civi_cancel_status || '—'}</TableCell>
                <TableCell>{dateTime(r.migrated_at)}</TableCell>
                <TableCell>{dateTime(r.created_at)}</TableCell>
                <TableCell>{dateTime(r.updated_at)}</TableCell>
                <TableCell sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxWidth: 360 }}>{r.migration_error || '—'}</TableCell>
                <TableCell>{dateTime(r.contribution_date)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={recordMigOpen}
        onClose={recordMigSubmitting ? undefined : closeRecordMigrationDialog}
        maxWidth="xl"
        fullWidth
        PaperProps={{ sx: { minWidth: { md: 1120 } } }}
      >
        <DialogTitle>
          {recordMigRows.length > 1 ? `Group migrate ${recordMigRows.length} contributions` : 'Migrate contribution record'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Same flow as &quot;Manual Migration&quot; on product mapping: pick Woo product/variation, then proceed. Does not change the mapping table.
            </Typography>
            <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 1, maxHeight: 240 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Contribution ID</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Contribution date</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell>Display name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>User ID</TableCell>
                    <TableCell>Payment method</TableCell>
                    <TableCell>Source</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recordMigRows.map((c, idx) => (
                    <TableRow
                      key={`mig-row-${contributionIdOf(c) || idx}`}
                      sx={(() => {
                        const st = contributionRunStatus(contributionIdOf(c));
                        if (st === 'completed') return { bgcolor: 'rgba(46, 125, 50, 0.08)' };
                        if (st === 'failed') return { bgcolor: 'rgba(211, 47, 47, 0.08)' };
                        return undefined;
                      })()}
                    >
                      <TableCell>{String(contributionIdOf(c) || '—')}</TableCell>
                      <TableCell>{renderContributionStatusIcon(contributionRunStatus(contributionIdOf(c)))}</TableCell>
                      <TableCell>{dateTime(c.contribution_date || c.contributionDate)}</TableCell>
                      <TableCell align="right">{money(c.contribution_amount || c.contributionAmount)}</TableCell>
                      <TableCell>{String(c.customer_display_name || '—')}</TableCell>
                      <TableCell>{String(c.customer_email || c.customerEmail || '—')}</TableCell>
                      <TableCell>{String(c.wp_user_id ?? c.userId ?? '—')}</TableCell>
                      <TableCell>{String(c.payment_method || c.paymentMethod || '—')}</TableCell>
                      <TableCell sx={{ maxWidth: 200 }}>
                        {c.source != null && String(c.source).trim() !== '' ? String(c.source) : '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Typography variant="subtitle2" color="text.secondary" sx={{ pt: 0.5 }}>
              WooCommerce mapping for this migration run
            </Typography>
            <Autocomplete
              options={(() => {
                if (!recordMigSelectedProduct?.id) return recordMigProductOptions;
                const has = recordMigProductOptions.some((o) => Number(o?.id) === Number(recordMigSelectedProduct.id));
                return has ? recordMigProductOptions : [recordMigSelectedProduct, ...recordMigProductOptions];
              })()}
              getOptionLabel={(o) => (o?.name ? `${o.name} (#${o.id})` : '')}
              value={recordMigSelectedProduct}
              onChange={(_, v) => setRecordMigSelectedProduct(v)}
              inputValue={recordMigProductInput}
              onInputChange={(_, v) => setRecordMigProductInput(v)}
              renderInput={(params) => (
                <TextField {...params} label="Search product name" placeholder="Type at least 2 characters" />
              )}
              isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
            />
            {recordMigSelectedProduct?.id != null && (
              <>
                {recordMigVariations.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No published variations for this product — migration will use product ID only.
                  </Typography>
                ) : (
                  <FormControl fullWidth size="small">
                    <InputLabel id="migrated-mig-var-select-label">Variation (required)</InputLabel>
                    <Select
                      labelId="migrated-mig-var-select-label"
                      label="Variation (required)"
                      value={recordMigVariationId}
                      onChange={(e) => setRecordMigVariationId(e.target.value)}
                      renderValue={(val) => {
                        if (val === '') return 'Select variation…';
                        const v = recordMigVariations.find((x) => String(x.id) === String(val));
                        return v ? variationSelectLabelWithId(v) : val;
                      }}
                    >
                      <MenuItem value="" disabled>
                        <em>Select variation…</em>
                      </MenuItem>
                      {recordMigVariations.map((v, ix) => (
                        <MenuItem key={v.id} value={String(v.id)}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', py: 0.25 }}>
                            <Typography variant="body2" component="span">
                              {ix + 1}. {variationSelectLabelWithId(v)}
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </>
            )}
            {recordMigError && (
              <Typography color="error" variant="body2">
                {recordMigError}
              </Typography>
            )}
            {recordMigSummary && (
              <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 700 }}>
                Summary: queued {Number(recordMigSummary?.queued || 0)}, processing {Number(recordMigSummary?.processing || 0)}, completed {Number(recordMigSummary?.completed || 0)}, failed {Number(recordMigSummary?.failed || 0)}
              </Typography>
            )}
            {recordMigResult?.data?.length > 0 && (
              <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 1, maxHeight: 260 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Contribution ID</TableCell>
                      <TableCell>Woo order</TableCell>
                      <TableCell>Migration</TableCell>
                      <TableCell>Civi cancel</TableCell>
                      <TableCell>Product</TableCell>
                      <TableCell>Variation</TableCell>
                      <TableCell>Error</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recordMigResult.data.map((row) => (
                      <TableRow key={`record-mig-result-${row.contribution_id}`}>
                        <TableCell>{String(row.contribution_id || '—')}</TableCell>
                        <TableCell>{String(row.wc_order_id || '—')}</TableCell>
                        <TableCell>{String(row.migration_status || '—')}</TableCell>
                        <TableCell>{String(row.civi_cancel_status || '—')}</TableCell>
                        <TableCell>{row.wc_product_name ? `${row.wc_product_name} (#${row.wc_product_id})` : String(row.wc_product_id || '—')}</TableCell>
                        <TableCell>{row.wc_variation_name ? `${row.wc_variation_name} (#${row.wc_variation_id})` : String(row.wc_variation_id || '—')}</TableCell>
                        <TableCell>{row.migration_error ? String(row.migration_error) : '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={closeRecordMigrationDialog}
            disabled={
              recordMigSubmitting
              || (
                String(recordMigRunState || '').trim().toLowerCase() === 'running'
                || Number(recordMigSummary?.queued || 0) > 0
                || Number(recordMigSummary?.processing || 0) > 0
              )
            }
          >
            Close
          </Button>
          <Button variant="contained" onClick={onProceedRecordMigration} disabled={!canProceedRecordMigration}>
            {recordMigSubmitting || String(recordMigRunState || '').trim().toLowerCase() === 'running' ? 'Processing…' : 'Proceed'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Contribution details</DialogTitle>
        <DialogContent>
          {detailLoading && <Typography variant="body2">Loading...</Typography>}
          {!detailLoading && detailError && <Typography color="error">{detailError}</Typography>}
          {!detailLoading && !detailError && !detailRow && (
            <Typography variant="body2" color="text.secondary">No details found.</Typography>
          )}
          {!detailLoading && !detailError && detailRow && (
            <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 1, mt: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: '35%' }}>Field</TableCell>
                    <TableCell>Value</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.entries(detailRow).map(([k, v]) => (
                    <TableRow key={k}>
                      <TableCell sx={{ fontFamily: 'ui-monospace, monospace' }}>{k}</TableCell>
                      <TableCell sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {v == null ? 'NULL' : (typeof v === 'object' ? JSON.stringify(v, null, 2) : String(v))}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </PageLayout>
  );
}
